/*
  Upsell card driver.
  -------------------
  Runs the quick-add sheet behind every `snippets/upsell-card.liquid` card: opening
  the dialog, resolving the selected options to a variant, and handing the add off
  to `assets/cart.js`. It owns no cart state of its own — `window.themeCart` is the
  only thing that talks to the cart, and the sheet reacts to what comes back.

  Fully document-delegated, so cards that arrive later (theme editor re-render, a
  cart drawer fetched at runtime) work without re-initialising anything. The row a card
  sits in is assets/carousel.js's — including re-measuring one that opens inside a dialog.

  Markup contract — see snippets/upsell-card.liquid:
    [data-upsell-open="<dialog id>"]   the "+" chip
    [data-upsell-sheet]                the <dialog>
    [data-upsell-close]                close button
    [data-upsell-variants]             application/json array of
                                       {id, options, available, price, displayPrice, b2xPlain, rrpPlain, tracked, inventory}
    [data-upsell-option="<index>"]     an option group; its inputs are radios
    [data-upsell-option-label]         one value's label; carries `is-unavailable`
    [data-upsell-auto-option]          index of an option resolved here rather than rendered
    [data-upsell-auto-priority]        its values in preference order, comma separated
    [data-upsell-price]                price text, rewritten per variant
    [data-upsell-id]                   the hidden variant input the form submits
    [data-upsell-submit]               the add button
    [data-upsell-status]               aria-live region for success and errors

  One option may be resolved here instead of shown — the CN/VN sourcing split, named in
  Theme settings → Sourcing option. Its value is filled in from the priority order before
  the match runs, and resolution subtracts what the cart already holds, so each value is
  sold down to zero before the next is offered. Same rule as assets/product-form.js.

  The sheet is a real form posting to /cart/add, so with JS off the "+" is inert and
  nothing here runs. Everything below is progressive enhancement over that.
*/
(function () {
  if (window.__productUpsell) return;
  window.__productUpsell = true;

  var RESET_DELAY = 1600;

  function sheetFor(el) {
    return el.closest('[data-upsell-sheet]');
  }

  function variantsFor(sheet) {
    if (sheet.__upsellVariants) return sheet.__upsellVariants;
    var script = sheet.querySelector('[data-upsell-variants]');
    var parsed = [];
    if (script) {
      try {
        parsed = JSON.parse(script.textContent) || [];
      } catch (e) {
        parsed = [];
      }
    }
    sheet.__upsellVariants = parsed;
    return parsed;
  }

  function selectedOptions(sheet) {
    var groups = sheet.querySelectorAll('[data-upsell-option]');
    var values = [];
    for (var i = 0; i < groups.length; i++) {
      var index = parseInt(groups[i].getAttribute('data-upsell-option'), 10);
      var checked = groups[i].querySelector('input:checked');
      values[index] = checked ? checked.value : null;
    }
    return values;
  }

  // `loose` wildcards an unanswered option. Resolving *the* variant matches strictly; the
  // pool the withheld option is chosen from must not, or nothing reaches it.
  function matches(variant, wanted, loose) {
    var options = variant.options || [];
    for (var i = 0; i < wanted.length; i++) {
      if (wanted[i] === null || wanted[i] === undefined) {
        if (loose) continue;
        return false;
      }
      if (options[i] !== wanted[i]) return false;
    }
    return true;
  }

  /* The option the card withheld from the pickers, and the order to try its values in.
     Null when no option is withheld, which is the default. */
  function autoConfig(sheet) {
    if (sheet.__upsellAuto !== undefined) return sheet.__upsellAuto;

    var index = parseInt(sheet.getAttribute('data-upsell-auto-option'), 10);
    var conf = null;

    if (index >= 0) {
      var raw = (sheet.getAttribute('data-upsell-auto-priority') || '').split(',');
      var order = [];
      for (var i = 0; i < raw.length; i++) {
        var value = raw[i].trim().toLowerCase();
        if (value) order.push(value);
      }
      conf = { index: index, order: order };
    }

    sheet.__upsellAuto = conf;
    return conf;
  }

  /* What the cart already holds, per variant. Without this the resolver keeps offering a
     value the customer has already taken every unit of, and every further add is rejected. */
  function cartCounts() {
    var state = window.themeCart && window.themeCart.state();
    var items = (state && state.items) || [];
    var counts = {};
    for (var i = 0; i < items.length; i++) {
      counts[items[i].id] = (counts[items[i].id] || 0) + items[i].quantity;
    }
    return counts;
  }

  // Headroom, not just sellability: a variant on "continue selling" reports available at
  // zero, and would hold the queue at the first value for ever.
  function inStock(variant, counts) {
    if (!variant.available) return false;
    if (!variant.tracked) return true;
    return variant.inventory - ((counts && counts[variant.id]) || 0) > 0;
  }

  /* Whether one more can go in the cart — a different question from inStock(), which
     answers "which value still has units" for the resolver's queue. A variant set to keep
     selling at zero has no units to count but can always be bought, so gating the add on
     inStock() would refuse it with an empty cart. */
  function canAdd(variant, counts) {
    if (!variant.available) return false;
    if (variant.oversell || !variant.tracked) return true;
    return variant.inventory - ((counts && counts[variant.id]) || 0) > 0;
  }

  /* Fills the withheld option in: first priority value with stock, then any with stock, then
     anything sellable, then whatever exists so a sold-out size still prices. */
  function resolveAuto(sheet, wanted) {
    var conf = autoConfig(sheet);
    if (!conf) return wanted;

    var variants = variantsFor(sheet);
    var probe = wanted.slice();
    probe[conf.index] = null;

    var pool = [];
    for (var v = 0; v < variants.length; v++) {
      if (matches(variants[v], probe, true)) pool.push(variants[v]);
    }
    if (!pool.length) return wanted;

    var counts = cartCounts();
    var chosen = null;
    for (var p = 0; p < conf.order.length && !chosen; p++) {
      for (var i = 0; i < pool.length && !chosen; i++) {
        var value = String(pool[i].options[conf.index] || '').toLowerCase();
        if (value === conf.order[p] && inStock(pool[i], counts)) chosen = pool[i];
      }
    }
    for (var s = 0; s < pool.length && !chosen; s++) {
      if (inStock(pool[s], counts)) chosen = pool[s];
    }
    for (var a = 0; a < pool.length && !chosen; a++) {
      if (pool[a].available) chosen = pool[a];
    }

    chosen = chosen || pool[0];
    probe[conf.index] = chosen.options[conf.index];
    return probe;
  }

  function matchVariant(sheet) {
    var variants = variantsFor(sheet);
    // No option groups at all — a single-variant product, or one whose only option is the
    // withheld one. The resolver still has to run in the second case.
    if (!sheet.querySelector('[data-upsell-option]') && !autoConfig(sheet)) return variants[0] || null;

    var wanted = resolveAuto(sheet, selectedOptions(sheet));
    for (var i = 0; i < variants.length; i++) {
      if (matches(variants[i], wanted)) return variants[i];
    }
    return null;
  }

  /* ---------- Availability ----------
     Which values are reachable from the rest of the selection: the option's own position is
     wildcarded, never the others, so each row answers for itself. */
  function markAvailability(sheet) {
    var wanted = selectedOptions(sheet);
    var variants = variantsFor(sheet);
    var groups = sheet.querySelectorAll('[data-upsell-option]');
    var auto = autoConfig(sheet);

    for (var g = 0; g < groups.length; g++) {
      var index = parseInt(groups[g].getAttribute('data-upsell-option'), 10);
      var labels = groups[g].querySelectorAll('[data-upsell-option-label]');

      for (var l = 0; l < labels.length; l++) {
        var value = labels[l].getAttribute('data-upsell-option-label');
        var probe = wanted.slice();
        probe[index] = value;
        // A size is reachable if *any* value of the withheld option still has it, so it is
        // struck through only once every one of them is gone.
        if (auto) probe[auto.index] = null;

        var reachable = false;
        for (var v = 0; v < variants.length; v++) {
          if (!variants[v].available) continue;
          if (matches(variants[v], probe, true)) {
            reachable = true;
            break;
          }
        }
        labels[l].classList.toggle('is-unavailable', !reachable);
      }
    }
  }

  // A sparse matrix can strand the customer on a combination no variant has, every pill
  // selected and nothing to add. Hold the value just picked and move the other groups to the
  // nearest variant carrying it; the withheld option is resolved above, never repaired.
  function repair(sheet, changed) {
    var variants = variantsFor(sheet);
    var wanted = selectedOptions(sheet);

    var pool = [];
    for (var i = 0; i < variants.length; i++) {
      if ((variants[i].options || [])[changed] === wanted[changed]) pool.push(variants[i]);
    }
    if (!pool.length) return;

    // In stock first: a repair that lands on a sold-out variant trades one dead end for
    // another. Falling back to the first match keeps the pills in a real combination.
    var target = null;
    for (var p = 0; p < pool.length && !target; p++) {
      if (pool[p].available) target = pool[p];
    }
    target = target || pool[0];

    var groups = sheet.querySelectorAll('[data-upsell-option]');
    for (var g = 0; g < groups.length; g++) {
      var index = parseInt(groups[g].getAttribute('data-upsell-option'), 10);
      if (index === changed) continue;
      var value = (target.options || [])[index];
      var inputs = groups[g].querySelectorAll('input');
      // Matched by value rather than by a selector: an option value is merchant copy and
      // may carry quotes.
      for (var n = 0; n < inputs.length; n++) {
        if (inputs[n].value === value) inputs[n].checked = true;
      }
    }
  }

  function setStatus(sheet, message, tone) {
    var status = sheet.querySelector('[data-upsell-status]');
    if (!status) return;
    status.textContent = message || '';
    status.classList.toggle('is-error', tone === 'error');
    status.classList.toggle('is-success', tone === 'success');
  }

  // One place decides what the button says and whether it is live, so a variant
  // change and a failed add cannot leave it disagreeing with itself.
  function sync(sheet) {
    var variant = matchVariant(sheet);
    var input = sheet.querySelector('[data-upsell-id]');
    var submit = sheet.querySelector('[data-upsell-submit]');
    var label = submit && submit.querySelector('.button__label');

    sheet.__upsellVariant = variant;
    if (input) input.value = variant ? variant.id : '';
    markAvailability(sheet);

    var priceRoot = sheet.querySelector('[data-upsell-price]');
    if (priceRoot && variant) {
      var compare = priceRoot.querySelector('[data-upsell-price-compare]');
      var was = priceRoot.querySelector('[data-upsell-price-was]');
      var current = priceRoot.querySelector('[data-upsell-price-current]');
      var b2x = !!variant.b2xPlain;
      if (compare) compare.hidden = !b2x;
      if (was) was.textContent = b2x ? variant.rrpPlain || '' : '';
      if (current) {
        current.textContent = b2x ? variant.b2xPlain : variant.displayPrice || variant.price || '';
        current.classList.toggle('upsell-card__sheet-price-text--sale', b2x);
      }
    }

    if (!submit) return;

    // Sellable is not the same as addable: the last unit may already be in the cart, and
    // `available` cannot see that. Stopping here beats letting the add fail — Shopify's
    // cart errors quote the variant title, which would print the withheld option.
    var spent = variant && variant.available && !canAdd(variant, cartCounts());
    var unavailable = !variant || !variant.available || spent;
    submit.disabled = unavailable;
    submit.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
    submit.classList.toggle('button--disabled', unavailable);

    if (!label) return;
    if (!variant) label.textContent = submit.getAttribute('data-label-choose') || '';
    else if (!variant.available) label.textContent = submit.getAttribute('data-label-sold-out') || '';
    else if (spent) {
      label.textContent = submit.getAttribute('data-label-max') || submit.getAttribute('data-label-sold-out') || '';
    } else label.textContent = submit.getAttribute('data-label-add') || '';
  }

  function openSheet(id, trigger) {
    var sheet = document.getElementById(id);
    if (!sheet || typeof sheet.showModal !== 'function') return;
    // Every close path (Esc, backdrop, button) fires `close`, so park the return
    // target on the dialog rather than tracking each exit separately.
    sheet.__upsellReturnFocus = trigger;
    setStatus(sheet, '');
    sync(sheet);
    sheet.showModal();
    var first = sheet.querySelector('[data-upsell-option] input:checked, [data-upsell-submit], [data-upsell-close]');
    if (first) first.focus();
  }

  // Back to the truth: a rejected add may mean the variant sold out between the sheet
  // opening and the click.
  function addFailed(sheet, error) {
    var submit = sheet.querySelector('[data-upsell-submit]');
    if (submit) submit.classList.remove('is-loading');

    // Shopify's cart errors quote the variant title — "…Supernova / XXL" — so the theme's
    // own wording stands in whenever an option is being withheld.
    var fallback = sheet.getAttribute('data-error-fallback') || '';
    setStatus(sheet, autoConfig(sheet) ? fallback : error.message || fallback, 'error');
    sync(sheet);
  }

  // The B2X line item properties `snippets/b2x-properties.liquid` renders into the form.
  function b2xProperties(scope) {
    var inputs = scope ? scope.querySelectorAll('[data-b2x-property]') : [];
    if (!inputs.length) return null;
    var out = {};
    for (var i = 0; i < inputs.length; i++) {
      out[inputs[i].name.replace(/^properties\[|\]$/g, '')] = inputs[i].value;
    }
    return out;
  }

  function addToCart(sheet, variant, quantity, mayRetry) {
    var submit = sheet.querySelector('[data-upsell-submit]');
    var label = submit && submit.querySelector('.button__label');

    if (submit) {
      submit.disabled = true;
      submit.classList.add('is-loading');
    }
    setStatus(sheet, '');

    window.themeCart
      .add(
        { id: variant.id, quantity: quantity, properties: b2xProperties(sheet) },
        sheet.getAttribute('data-upsell-source') || 'product-upsell'
      )
      .then(function () {
        // The button's own label is the confirmation. A second copy of it in the status
        // line said the same thing twice; the line is kept for errors, which the button
        // cannot carry.
        if (label) label.textContent = submit.getAttribute('data-label-added') || '';
        if (submit) submit.classList.remove('is-loading');
        // Long enough for the confirmation to register, short enough that the
        // sheet does not sit in the way of the journey it just added to.
        sheet.__upsellCloseTimer = setTimeout(function () {
          if (sheet.open) sheet.close();
        }, RESET_DELAY);
      })
      .catch(function (error) {
        /* A cart populated before this page loaded is invisible to the resolver — its
           state is empty until themeCart has read the cart once — so the first add can be
           rejected for a value it thinks has headroom. Re-read the cart, re-resolve, and
           try the next value once. Without this the retry picks the same variant again. */
        if (!mayRetry || !autoConfig(sheet)) return addFailed(sheet, error);

        window.themeCart
          .refresh(sheet.getAttribute('data-upsell-source') || 'product-upsell')
          .then(function () {
            var next = matchVariant(sheet);
            if (next && next.id !== variant.id && next.available) {
              return addToCart(sheet, next, quantity, false);
            }
            addFailed(sheet, error);
          })
          .catch(function () {
            addFailed(sheet, error);
          });
      });
  }

  function submitSheet(event, form) {
    var sheet = sheetFor(form);
    if (!sheet || !window.themeCart) return;

    event.preventDefault();

    var variant = sheet.__upsellVariant;
    if (!variant || !variant.available) return;

    var quantity = parseInt((form.querySelector('[name="quantity"]') || {}).value, 10) || 1;
    addToCart(sheet, variant, quantity, true);
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    var opener = e.target.closest('[data-upsell-open]');
    if (opener) {
      openSheet(opener.getAttribute('data-upsell-open'), opener);
      return;
    }

    var closer = e.target.closest('[data-upsell-close]');
    if (closer) {
      var owner = sheetFor(closer);
      if (owner) owner.close();
      return;
    }

    // A backdrop click targets the dialog element itself.
    if (e.target.matches && e.target.matches('[data-upsell-sheet]')) {
      e.target.close();
    }
  });

  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.closest) return;
    var group = e.target.closest('[data-upsell-option]');
    if (!group) return;
    var sheet = sheetFor(e.target);
    if (!sheet) return;
    if (!matchVariant(sheet)) repair(sheet, parseInt(group.getAttribute('data-upsell-option'), 10));
    setStatus(sheet, '');
    sync(sheet);
  });

  document.addEventListener('submit', function (e) {
    if (!e.target || !e.target.matches) return;
    if (!e.target.matches('[data-upsell-form]')) return;
    submitSheet(e, e.target);
  });

  // `close` does not bubble — listen in the capture phase.
  document.addEventListener(
    'close',
    function (e) {
      var sheet = e.target;
      if (!sheet || !sheet.matches || !sheet.matches('[data-upsell-sheet]')) return;

      clearTimeout(sheet.__upsellCloseTimer);
      setStatus(sheet, '');
      // Back to the resting label, or a reopened sheet still reads "Added".
      sync(sheet);

      var target = sheet.__upsellReturnFocus;
      sheet.__upsellReturnFocus = null;
      if (target && document.contains(target)) target.focus();
    },
    true
  );

  /* ---------- Theme editor lifecycle ---------- */
  /* Drop the return target first: the card is still attached here, so the close
     handler would otherwise focus a chip that is about to be removed. */
  document.addEventListener('shopify:section:unload', function (e) {
    var sheets = e.target.querySelectorAll('[data-upsell-sheet]');
    for (var i = 0; i < sheets.length; i++) {
      clearTimeout(sheets[i].__upsellCloseTimer);
      sheets[i].__upsellReturnFocus = null;
      if (sheets[i].open) sheets[i].close();
    }
  });
})();
